import { useEffect, useRef } from 'react';

import useDTO from 'components/Map/hooks/useDTO';
import useMapContext from 'components/Map/hooks/useMapContext';
import useUndo from 'components/Map/hooks/useUndo';

import useValidateDrawing from './hooks/useValidateDrawing';

import {
    COMPLETED_LAYER_BY_MODE,
    DRAWING_MODE,
    DRAWING_TOOL,
    GEOJSON_PROPERTY,
    MAP_ACTION_TYPE
} from './constants';
import useCircle from './tools/circle/index';
import usePolygon from './tools/polygon/index';
import type { BoundaryPartData, DrawingTool, Feature, Polygon, Props } from './types';

const useDrawingTool = ({ onDrawingCompleted, onDrawingUpdated }: Props) => {
    const { validateBoundaryPolygon, validateObstaclePolygon } = useValidateDrawing();
    const { convertFromDTOs } = useDTO();

    const { addAction, clearActions: clearUndoRedo, redo, undo } = useUndo();

    const { features, mapState } = useMapContext();
    const { updateFeature } = features;
    const { drawingMode, drawingTool, selectedFeature, setIsDrawing, setMapErrors } = mapState;

    const VALIDATION_MAP = {
        [DRAWING_MODE.APPLY]: validateBoundaryPolygon,
        [DRAWING_MODE.NO_ENTRY]: validateObstaclePolygon
    };

    const pendingPartIndexRef = useRef<number | undefined>(undefined);

    const handleDrawingCompleted = (feature: Feature) => {
        const errors = VALIDATION_MAP[drawingMode](feature as Feature<Polygon>);

        setMapErrors(errors);

        if (errors.length === 0) {
            resetDrawing();

            const drawingMode = feature.properties?.[
                GEOJSON_PROPERTY.BOUNDARY_TYPE
            ] as DRAWING_MODE;

            if (pendingPartIndexRef.current !== undefined && feature.properties) {
                feature.properties[GEOJSON_PROPERTY.PART_INDEX] = pendingPartIndexRef.current;
                pendingPartIndexRef.current = undefined;
            }

            updateFeature(feature, COMPLETED_LAYER_BY_MODE[drawingMode]);

            feature.properties[GEOJSON_PROPERTY.IS_SELECTED] = false;
            onDrawingCompleted(feature);
            setIsDrawing(false);
        } else {
            // We still have errors so reload the feature for editing
            loadDrawing(feature);
        }
    };

    const handleDrawingUpdated = (feature: Feature) => {
        onDrawingUpdated(feature);
    };

    const polygonTool = usePolygon({
        onCompleteFeature: handleDrawingCompleted,
        onUpdateDrawing: handleDrawingUpdated,
        onUpdatePolygonPoints: (polygon: Feature<Polygon>, isNewDrawing: boolean) => {
            addAction({
                isNewDrawing,
                polygon,
                type: MAP_ACTION_TYPE.POLYGON_UPDATE_POINTS
            });
        }
    });

    const circleTool = useCircle({
        onCompleteFeature: handleDrawingCompleted,
        onUpdateCirclePoints: (circle: Feature<Polygon>) => {
            addAction({
                circle,
                type: MAP_ACTION_TYPE.CIRCLE_UPDATE_POINTS
            });
        },
        onUpdateDrawing: handleDrawingUpdated
    });

    const drawingToolMap: Record<DRAWING_TOOL, DrawingTool> = {
        [DRAWING_TOOL.POLYGON]: polygonTool,
        [DRAWING_TOOL.CIRCLE]: circleTool
    };

    const currentDrawingTool = drawingToolMap[drawingTool];
    const { completeDrawing, handleClick, handleMapAction, isDrawing, loadDrawing, resetDrawing } =
        currentDrawingTool;

    const loadExternalGeometry = (geometry?: BoundaryPartData[]) => {
        if (!geometry || geometry.length === 0) {
            return;
        }

        const features = convertFromDTOs(geometry);

        features.forEach((feature, partIndex) => {
            pendingPartIndexRef.current = partIndex;

            const { completeDrawing, loadDrawing } =
                drawingToolMap[feature.properties?.[GEOJSON_PROPERTY.POLYGON_TYPE] as DRAWING_TOOL];

            loadDrawing(feature);
            completeDrawing();
        });

        pendingPartIndexRef.current = undefined;
    };

    useEffect(() => {
        if (!isDrawing) {
            clearUndoRedo();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDrawing]);

    useEffect(() => {
        const { resetDrawing: resetPolygonTool } = polygonTool;

        resetPolygonTool();

        const { resetDrawing: resetCircleTool } = circleTool;

        resetCircleTool();

        clearUndoRedo();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [drawingTool, drawingMode]);

    useEffect(() => {
        clearUndoRedo();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFeature]);

    const handleUndo = () => {
        const action = undo();

        if (action) {
            handleMapAction(action);
        }
    };

    const handleRedo = () => {
        const action = redo();

        if (action) {
            handleMapAction(action);
        }
    };

    const handleLoadDrawing = (feature: Feature) => {
        const { loadDrawing } =
            drawingToolMap[feature.properties?.[GEOJSON_PROPERTY.POLYGON_TYPE] as DRAWING_TOOL];

        loadDrawing(feature);
    };

    const resetDrawingState = () => {
        polygonTool.resetDrawing();
        circleTool.resetDrawing();
    };

    return {
        clearUndoRedo,
        handleClick,
        handleCompleteDrawing: completeDrawing,
        handleDrawingUpdated,
        handleLoadDrawing,
        handleResetDrawing: resetDrawing,
        loadExternalGeometry,
        redo: handleRedo,
        resetDrawingState,
        undo: handleUndo
    };
};

export default useDrawingTool;
